import React, { useEffect } from 'react';
import { Select } from 'antd';
import { useLocation, useNavigate } from 'react-router';

import { WarningOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from '@tt/dates/dayjs';

import ViolationExplanation from '../../../../components/violation-explanation';
import { useManagementStore, useSettingStore, useUserState } from '../../../../store';
import { getCompanyFromAccountUniqueId } from '../../../../utilities/account-ids';
import axios from '../../../../utilities/axios';
import usePageTitle from '../../../../utilities/hooks/usePageTitle';
import FilterHeader from '../filter-header';

import './styles.scss';

const ContentHeader = () => {
    const navigate = useNavigate();
    const { viewAs, setViewAs, allUsers, setAllUsers, company, setCompany } = useManagementStore(
        (state) => state
    );
    const { getUser } = useUserState();
    const { filter } = useSettingStore((state) => state);
    const user = getUser();
    const { pathname } = useLocation();
    const isDashboard = pathname === '/';
    const pageTitle = usePageTitle();
    const UNASSIGNED_OPTION_PREFIX = 'unassigned::';
    const unassignedRouteMatch = pathname.match(/^\/admin\/u\/([^/]+)/i);
    const currentUnassignedId = unassignedRouteMatch ? unassignedRouteMatch[1] : null;
    const normalizedCurrentUnassignedId = currentUnassignedId
        ? currentUnassignedId.toUpperCase()
        : null;
    const isUnassignedRoute = Boolean(normalizedCurrentUnassignedId);

    const fetchUsers = () =>
        axios.get('crud/users', { headers: { token: user?.token } }).then(({ data }) => data);
    const fetchUnassignedAccounts = () =>
        axios
            .get('crud/unassigned-live-accounts', {
                headers: { token: user?.token },
                params: { company }
            })
            .then(({ data }) => data);
    const normalizeUserId = (value) => (typeof value === 'string' ? value.split('::')[0] : value);

    const selectedFilterAccountId =
        Array.isArray(filter?.accounts) && filter.accounts.length > 0 ? filter.accounts[0] : null;

    const fetchFundedAccountDoc = async (id) =>
        axios
            .get(`crud/funded-accounts/document/${id}`, { headers: { token: user?.token } })
            .then(({ data }) => data)
            .catch(() => null);

    const { data: fundedAccountDoc } = useQuery(
        ['funded-account-doc', selectedFilterAccountId],
        () => (selectedFilterAccountId ? fetchFundedAccountDoc(selectedFilterAccountId) : null),
        { enabled: !!selectedFilterAccountId && !!user?.token }
    );

    const { isFetched: hasFetchedUsers } = useQuery(
        ['users', company],
        () =>
            Promise.all([fetchUsers(), fetchUnassignedAccounts().catch(() => [])]).then(
                async ([users, unassignedAccounts]) => {
                    try {
                        let userSelectionFormat = [];

                        if (Array.isArray(users) && users.length > 0) {
                            if (company === 'Bear Bull Traders') {
                                userSelectionFormat =
                                    users?.flatMap((u) => {
                                        if (u.fundedAccounts?.length > 0) {
                                            return u.fundedAccounts
                                                .filter((a) => a.type === 'ptt')
                                                .map((a, idx) => ({
                                                    value: `${u._id}::${
                                                        a.accountUniqueId || a._id || idx
                                                    }`,
                                                    userId: u._id,
                                                    label: `${a.accountUniqueId} - ${
                                                        u.email || 'Unknown'
                                                    }`,
                                                    mode: 'user'
                                                }));
                                        }
                                        return [];
                                    }) || [];
                            } else if (company === 'Peak Capital Trading') {
                                userSelectionFormat =
                                    users?.flatMap((u) => {
                                        if (u.fundedAccounts?.length > 0) {
                                            return u.fundedAccounts
                                                .filter(
                                                    (a) => (a.type || '').toLowerCase() === 'pct'
                                                )
                                                .map((a, idx) => ({
                                                    value: `${u._id}::${
                                                        a.accountUniqueId || a._id || idx
                                                    }`,
                                                    userId: u._id,
                                                    label: `${a.accountUniqueId} - ${
                                                        u.email || 'Unknown'
                                                    }`,
                                                    mode: 'user'
                                                }));
                                        }
                                        return [];
                                    }) || [];
                            } else if (company === 'Trading Terminal') {
                                userSelectionFormat =
                                    users?.flatMap((u) => {
                                        const entries = [];
                                        if (u.fundedAccounts?.length > 0) {
                                            u.fundedAccounts
                                                .filter(
                                                    (a) =>
                                                        (a.type || '').toLowerCase() ===
                                                        'simulator_ttd'
                                                )
                                                .forEach((a) => {
                                                    entries.push({
                                                        value: `${u._id}::${
                                                            a.accountUniqueId ||
                                                            a._id ||
                                                            entries.length
                                                        }`,
                                                        userId: u._id,
                                                        label: `${a.accountUniqueId} - ${
                                                            u.email || 'Unknown'
                                                        }`,
                                                        mode: 'user'
                                                    });
                                                });

                                            u.fundedAccounts
                                                .filter(
                                                    (a) =>
                                                        (a.type || '').toLowerCase() ===
                                                        'simulator_ttf'
                                                )
                                                .forEach((a) => {
                                                    entries.push({
                                                        value: `${u._id || u.id}::${
                                                            a.accountUniqueId ||
                                                            a._id ||
                                                            entries.length
                                                        }`,
                                                        userId: u._id || u.id,
                                                        label: `${a.accountUniqueId} - ${
                                                            u.email || 'Unknown'
                                                        }`,
                                                        mode: 'user'
                                                    });
                                                });
                                        }
                                        return entries;
                                    }) || [];
                            }
                        }

                        const unassignedSelectionFormat = (
                            Array.isArray(unassignedAccounts) ? unassignedAccounts : []
                        )
                            .filter((entry) => entry?.accountUniqueId)
                            .map((entry) => ({
                                value: `${UNASSIGNED_OPTION_PREFIX}${entry.accountUniqueId}`,
                                userId: `${UNASSIGNED_OPTION_PREFIX}${entry.accountUniqueId}`,
                                accountUniqueId: entry.accountUniqueId,
                                label: `${entry.accountUniqueId}`,
                                mode: 'unassigned'
                            }));

                        const sortedUsers = [...userSelectionFormat, ...unassignedSelectionFormat]
                            .filter((u) => u && typeof u === 'object' && 'label' in u)
                            .sort((a, b) => {
                                if (a.mode === 'unassigned' && b.mode !== 'unassigned') return -1;
                                if (a.mode !== 'unassigned' && b.mode === 'unassigned') return 1;

                                const aIncludes =
                                    a.label.includes('TRPCT') ||
                                    a.label.includes('TRPTT') ||
                                    a.label.includes('TTD') ||
                                    a.label.includes('TT') ||
                                    a.label.includes('TTF');
                                const bIncludes =
                                    b.label.includes('TRPCT') ||
                                    b.label.includes('TRPTT') ||
                                    b.label.includes('TTD') ||
                                    b.label.includes('TT') ||
                                    b.label.includes('TTF');

                                if (aIncludes && !bIncludes) return -1;
                                if (!aIncludes && bIncludes) return 1;

                                return a.label.localeCompare(b.label);
                            });

                        setAllUsers(sortedUsers);
                        return users;
                    } catch (error) {
                        setAllUsers([]);
                        return [];
                    }
                }
            ),
        { enabled: user.token !== undefined && user.role === 'admin' }
    );

    const onChange = async (newValue, option) => {
        try {
            if (option?.mode === 'unassigned') {
                const selectedAccountUniqueId =
                    option?.accountUniqueId ||
                    String(newValue || '').replace(UNASSIGNED_OPTION_PREFIX, '');
                if (!selectedAccountUniqueId) return;
                const inferredCompany = getCompanyFromAccountUniqueId(selectedAccountUniqueId);

                if (inferredCompany) {
                    setCompany(inferredCompany);
                }

                setViewAs(
                    { token: user?.token, id: user?.id },
                    { token: user?.token, id: user?.id }
                );
                navigate(`/admin/u/${selectedAccountUniqueId.toUpperCase()}`);
                return;
            }

            const newId = option?.userId || normalizeUserId(newValue);

            // Fetch the token for the selected user
            const response = await axios.get(`/crud/users/${newId}/token`, {
                headers: { token: user?.token }
            });

            const newToken = response.data.token; // Adjust based on your API response structure

            setViewAs({ token: newToken, id: newId }, { token: user?.token, id: user?.id });
            if (!isDashboard) navigate('/');
        } catch (error) {
            console.error('Failed to fetch user token:', error);
            // Optionally show an error message to the user
        }
    };

    const filterOption = (input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
    const normalizedViewAsId = normalizeUserId(viewAs?.newUser?.id);
    const selectedViewAsOptionValue = isUnassignedRoute
        ? allUsers.find(
              (u) =>
                  u.mode === 'unassigned' &&
                  `${u.accountUniqueId || ''}`.toUpperCase() === normalizedCurrentUnassignedId
          )?.value
        : allUsers.find((u) => u.mode !== 'unassigned' && u.userId === normalizedViewAsId)?.value;
    const shouldShowFilterHeader =
        !pathname.startsWith('/settings') &&
        pathname !== '/compare' &&
        pathname !== '/add-trade' &&
        !pathname.startsWith('/trades/');
    const shouldShowViewAsSelector =
        user.role === 'admin' &&
        company !== 'Personal' &&
        Array.isArray(allUsers) &&
        allUsers.length > 0;
    const shouldShowAccountDates = Boolean(fundedAccountDoc);
    const shouldShowViolation = fundedAccountDoc?.riskViolation === true;
    const shouldShowMetaRow =
        shouldShowViewAsSelector || shouldShowAccountDates || shouldShowViolation;

    useEffect(() => {
        if (
            user.role !== 'admin' ||
            !['Bear Bull Traders', 'Peak Capital Trading', 'Trading Terminal'].includes(company) ||
            !hasFetchedUsers ||
            allUsers.length === 0
        )
            return;
        if (isUnassignedRoute) return;

        const selectedUserExists = allUsers.some(
            (u) => u.mode !== 'unassigned' && u.userId === normalizedViewAsId
        );
        if (selectedUserExists) return;

        const currentUserOption = allUsers.find(
            (u) => u.mode !== 'unassigned' && u.userId === user?.id
        );
        const firstRealUserOption = allUsers.find((u) => u.mode !== 'unassigned');
        const defaultUserId = currentUserOption?.userId || firstRealUserOption?.userId;
        if (!defaultUserId) return;

        axios
            .get(`/crud/users/${defaultUserId}/token`, {
                headers: { token: user?.token }
            })
            .then((response) => {
                const defaultUserToken = response.data.token;
                setViewAs(
                    { token: defaultUserToken, id: defaultUserId },
                    { token: user?.token, id: user?.id }
                );
            })
            .catch((error) => {
                console.error('Failed to fetch initial user token:', error);
            });
    }, [
        allUsers,
        company,
        hasFetchedUsers,
        isUnassignedRoute,
        normalizedViewAsId,
        setViewAs,
        user?.id,
        user?.role,
        user?.token
    ]);

    return (
        <div className="content-header">
            <div className="content-header__top-row">
                <h1 className="content-header-title">{pageTitle}</h1>
                {shouldShowFilterHeader && <FilterHeader variant="inline" />}
            </div>

            {shouldShowMetaRow && (
                <div className="content-header__meta-row">
                    <div className="content-header__meta-left">
                        {shouldShowViewAsSelector && (
                            <div className="content-header__view-as">
                                <Select
                                    showSearch
                                    placeholder="Select a user or account"
                                    maxTagCount={1}
                                    style={{ minWidth: 220, maxWidth: 350 }}
                                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                                    value={selectedViewAsOptionValue}
                                    onChange={onChange}
                                    filterOption={filterOption}
                                    options={allUsers}
                                    optionFilterProp="label"
                                    optionLabelProp="label"
                                    allowClear={false}
                                    notFoundContent={<span>No users found</span>}
                                    dropdownRender={(menu) => (
                                        <div>
                                            <div
                                                style={{
                                                    padding: 8,
                                                    fontWeight: 500,
                                                    borderBottom: '1px solid #f0f0f0'
                                                }}
                                            >
                                                Select a user or account
                                            </div>
                                            {menu}
                                            <div
                                                style={{
                                                    padding: 8,
                                                    borderTop: '1px solid #f0f0f0',
                                                    color: '#888',
                                                    fontSize: 12
                                                }}
                                            >
                                                {allUsers.length} entries available
                                            </div>
                                        </div>
                                    )}
                                />
                            </div>
                        )}
                    </div>

                    <div className="content-header__meta-right">
                        {shouldShowAccountDates && (
                            <div className="content-header__account-dates">
                                {fundedAccountDoc.activationDate && (
                                    <span>
                                        <span className="content-header__account-date-label">
                                            Account Start Date:
                                        </span>{' '}
                                        {dayjs(fundedAccountDoc.activationDate).format(
                                            'MMM DD, YYYY'
                                        )}
                                    </span>
                                )}
                                {fundedAccountDoc.expirationDate && (
                                    <span>
                                        <span className="content-header__account-date-label">
                                            Account End Date:
                                        </span>{' '}
                                        {dayjs(fundedAccountDoc.expirationDate).format(
                                            'MMM DD, YYYY'
                                        )}
                                    </span>
                                )}
                            </div>
                        )}
                        {shouldShowViolation && (
                            <div className="content-header__violation">
                                <WarningOutlined className="content-header__violation-icon" />
                                <span className="content-header__violation-text">
                                    Not Eligible For Funded Account Payout
                                </span>
                                <div className="content-header__violation-action">
                                    <ViolationExplanation
                                        accountId={selectedFilterAccountId}
                                        buttonText="View Reasons"
                                        riskViolationReason={fundedAccountDoc?.riskViolationReason}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContentHeader;
